import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar } from "lucide-react";
import { loadBlogList } from "@/lib/content";
import { Rise } from "@/components/motion/Reveal";

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function BlogStrip() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    loadBlogList().then((list) => setPosts(list.slice(0, 3))).catch(() => setPosts([]));
  }, []);

  if (!posts.length) return null;

  return (
    <section
      id="blog"
      data-testid="blog-strip"
      className="py-24 lg:py-32 border-t border-[#E4E4E7] bg-[#F4F4F5] text-[#0A0A0A]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <Rise>
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">— Field Notes</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold leading-[0.95]">
              From the blog.
              <br />
              <span className="text-black/40">Straight from the trucks.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <Link
              to="/blog"
              data-testid="blog-strip-view-all"
              className="inline-flex items-center gap-2 border border-black/20 hover:border-black text-black font-bold px-5 py-3 text-sm transition-colors"
            >
              All articles <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
        </Rise>

        <motion.div
          className="grid md:grid-cols-3 gap-4 md:gap-6"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
        >
          {posts.map((p) => (
            <motion.div
              key={p.slug}
              variants={{
                hidden: { opacity: 0, y: 24 },
                show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
              }}
            >
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-strip-card-${p.slug}`}
                className="group flex flex-col border border-[#E4E4E7] bg-white hover:border-black transition-colors h-full"
              >
                <div className="aspect-[16/10] overflow-hidden border-b border-[#E4E4E7]">
                  {p.cover ? (
                    <img src={p.cover} alt={`${p.title} — ElectroMax electricians field notes cover`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full bg-[#EAEAEC]" />
                  )}
                </div>
                <div className="p-5 md:p-6 flex flex-col grow">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-black/40 mb-3 inline-flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> {fmtDate(p.date)}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight font-bold leading-tight text-black">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-black/60 line-clamp-3 leading-relaxed">
                    {p.excerpt}
                  </p>
                  <div className="mt-auto pt-5 flex items-center justify-end">
                    <ArrowUpRight className="w-4 h-4 text-black/40 group-hover:text-[#E60000] transition-colors" strokeWidth={2.5} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
